/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTenantData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading tenant data for edit/duplicate modes.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getTenantById } from '../api/tenantsApi';
import { DEFAULT_LANGUAGE, DEFAULT_TIMEZONE } from '../../../data/formOptions';

export interface TenantFormState {
  slug: string;
  name: string;
  description: string;
  adminEmail: string;
  defaultLocale: string;
  timezone: string;
  isActive: boolean;
  maxExtensions: number;
  maxTrunks: number;
  maxQueues: number;
  maxIvr: number;
  maxConferences: number;
  maxParkingLots: number;
  allowRecording: boolean;
}

export interface DomainFormState {
  domainName: string;
  domainDescription: string;
  language: string;
  timezone: string;
  countryCode: string;
}

export interface UserFormState {
  username: string;
  email: string;
  password: string;
  fullName: string;
  department: string;
  locale: string;
  timezone: string;
  startupDialog: string;
  totpEnabled: boolean;
  isActive: boolean;
  profileId: string;
  logProfileId: string;
  tenantIds: string[];
}

export const DEFAULT_TENANT_FORM: TenantFormState = {
  slug: '',
  name: '',
  description: '',
  adminEmail: '',
  defaultLocale: DEFAULT_LANGUAGE, // 'en-us-emma'
  timezone: DEFAULT_TIMEZONE,
  isActive: true,
  maxExtensions: 100,
  maxTrunks: 10,
  maxQueues: 5,
  maxIvr: 3,
  maxConferences: 2,
  maxParkingLots: 1,
  allowRecording: true
};

export const DEFAULT_DOMAIN_FORM: DomainFormState = {
  domainName: '',
  domainDescription: '',
  language: DEFAULT_LANGUAGE, // 'en-us-emma'
  timezone: DEFAULT_TIMEZONE,
  countryCode: '1'
};

export const DEFAULT_USER_FORM: UserFormState = {
  username: '',
  email: '',
  password: '',
  fullName: '',
  department: '',
  locale: 'es',
  timezone: 'UTC',
  startupDialog: 'dashboard',
  totpEnabled: false,
  isActive: true,
  profileId: '',
  logProfileId: '',
  tenantIds: []
};

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const clampNumber = (value: number, fallback: number, min = 0) =>
  Number.isFinite(value) && value >= min ? value : fallback;

const sanitizeTenantFormState = (state: TenantFormState): TenantFormState => {
  const trimmedName = state.name.trim();
  const trimmedSlug = state.slug.trim() || generateSlug(trimmedName);

  return {
    slug: trimmedSlug,
    name: trimmedName,
    description: state.description.trim(),
    adminEmail: state.adminEmail.trim(),
    defaultLocale: state.defaultLocale.trim() || DEFAULT_TENANT_FORM.defaultLocale,
    timezone: state.timezone.trim() || DEFAULT_TENANT_FORM.timezone,
    isActive: Boolean(state.isActive),
    maxExtensions: clampNumber(state.maxExtensions, DEFAULT_TENANT_FORM.maxExtensions),
    maxTrunks: clampNumber(state.maxTrunks, DEFAULT_TENANT_FORM.maxTrunks),
    maxQueues: clampNumber(state.maxQueues, DEFAULT_TENANT_FORM.maxQueues),
    maxIvr: clampNumber(state.maxIvr, DEFAULT_TENANT_FORM.maxIvr),
    maxConferences: clampNumber(state.maxConferences, DEFAULT_TENANT_FORM.maxConferences),
    maxParkingLots: clampNumber(state.maxParkingLots, DEFAULT_TENANT_FORM.maxParkingLots),
    allowRecording: Boolean(state.allowRecording)
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapTenantToFormState = (tenantData: any, duplicate = false): TenantFormState => {
  const baseName = tenantData.name ?? '';
  const baseDescription = tenantData.description ?? '';
  const baseSlug = tenantData.slug?.trim() || generateSlug(baseName);

  const formState: TenantFormState = {
    slug: duplicate ? `${baseSlug}_copy` : baseSlug,
    name: duplicate ? `${baseName}_copy` : baseName,
    description: baseDescription,
    adminEmail: tenantData.adminEmail ?? '',
    defaultLocale: tenantData.defaultLocale ?? DEFAULT_TENANT_FORM.defaultLocale,
    timezone: tenantData.timezone ?? DEFAULT_TENANT_FORM.timezone,
    isActive: tenantData.isActive ?? true,
    maxExtensions: tenantData.maxExtensions ?? DEFAULT_TENANT_FORM.maxExtensions,
    maxTrunks: tenantData.maxTrunks ?? DEFAULT_TENANT_FORM.maxTrunks,
    maxQueues: tenantData.maxQueues ?? DEFAULT_TENANT_FORM.maxQueues,
    maxIvr: tenantData.maxIvr ?? DEFAULT_TENANT_FORM.maxIvr,
    maxConferences: tenantData.maxConferences ?? DEFAULT_TENANT_FORM.maxConferences,
    maxParkingLots: tenantData.maxParkingLots ?? DEFAULT_TENANT_FORM.maxParkingLots,
    allowRecording: tenantData.allowRecording ?? DEFAULT_TENANT_FORM.allowRecording
  };

  return sanitizeTenantFormState(formState);
};

export function useTenantData(
  mode: 'create' | 'edit',
  id: string | undefined,
  duplicateId: string | null,
  resetParam?: string | null
) {
  const { user } = useAuthStore();
  const { showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || '';
  const tenantLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);

  const [formData, setFormData] = useState<TenantFormState>({ ...DEFAULT_TENANT_FORM });
  const [originalData, setOriginalData] = useState<TenantFormState>({ ...DEFAULT_TENANT_FORM });
  const [domainData, setDomainData] = useState<DomainFormState>({ ...DEFAULT_DOMAIN_FORM });
  const [userData, setUserData] = useState<UserFormState>({ ...DEFAULT_USER_FORM });
  const [isPrimary, setIsPrimary] = useState<boolean>(false);

  // Use refs for functions to avoid re-renders
  const setFormDataRef = useRef(setFormData);
  const setOriginalDataRef = useRef(setOriginalData);
  const setDomainDataRef = useRef(setDomainData);
  const setUserDataRef = useRef(setUserData);
  const setIsPrimaryRef = useRef(setIsPrimary);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  // Update refs when functions change
  useEffect(() => {
    setFormDataRef.current = setFormData;
    setOriginalDataRef.current = setOriginalData;
    setDomainDataRef.current = setDomainData;
    setUserDataRef.current = setUserData;
    setIsPrimaryRef.current = setIsPrimary;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [setFormData, setOriginalData, setDomainData, setUserData, setIsPrimary, showError, showInfo]);

  useEffect(() => {
    const loadTenant = async () => {
      const tenantId = duplicateId || id;
      if (!tenantId || tenantId === 'new') return;

      try {
        const result = await getTenantById(tenantSlug, user?.id ? Number(user.id) : 0, tenantId);
        const tenantData = result.data;

        const duplicate = Boolean(duplicateId);
        const formState = mapTenantToFormState(tenantData, duplicate);
        const originalState = mapTenantToFormState(tenantData, false);

        setFormDataRef.current(formState);
        setOriginalDataRef.current(originalState);

        // Set isPrimary flag (only for edit mode, not duplicate)
        if (!duplicate) {
          setIsPrimaryRef.current(tenantData.isPrimary ?? false);
        } else {
          setIsPrimaryRef.current(false);
        }

        // Show duplicate hint notification
        if (duplicate) {
          showInfoRef.current(t('tenants.messages.duplicateHint'));
        }
      } catch (error) {
        showErrorRef.current(error instanceof Error ? error.message : t('tenants.failedToLoadTenant'));
      }
    };

    const currentKey = `${mode}-${id || 'new'}-${duplicateId || 'none'}`;
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
    const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;

    // If reset param is present and not yet processed, force reset
    if (hasResetParam) {
      formResetRef.current = false;
      tenantLoadedRef.current = null;
      // Clear the lastIdRef to ensure we detect the change
      lastIdRef.current = undefined;
      // Mark this reset param as processed
      resetParamProcessedRef.current = resetParam;
    }

    if (mode === 'edit' || duplicateId) {
      if (tenantLoadedRef.current === currentKey && !hasResetParam && !idChangedToNew) {
        lastIdRef.current = currentId;
        return;
      }

      // Reset the form reset flag when loading an actual tenant
      formResetRef.current = false;
      loadTenant();
      tenantLoadedRef.current = currentKey;
      lastIdRef.current = currentId;
    } else if (mode === 'create') {
      // Reset form when navigating to new (either initial load or after save)
      if (hasResetParam || !formResetRef.current || idChangedToNew || tenantLoadedRef.current === null) {
        formResetRef.current = true;
        lastIdRef.current = currentId;
        tenantLoadedRef.current = 'create-initialized';
        setFormDataRef.current({ ...DEFAULT_TENANT_FORM });
        setOriginalDataRef.current({ ...DEFAULT_TENANT_FORM });
        setDomainDataRef.current({ ...DEFAULT_DOMAIN_FORM });
        setUserDataRef.current({ ...DEFAULT_USER_FORM });
      } else {
        lastIdRef.current = currentId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, duplicateId, tenantSlug, user?.id, resetParam]);

  return {
    formData,
    setFormData,
    originalData,
    setOriginalData,
    domainData,
    setDomainData,
    userData,
    setUserData,
    DEFAULT_TENANT_FORM,
    DEFAULT_DOMAIN_FORM,
    DEFAULT_USER_FORM,
    sanitizeTenantFormState,
    isPrimary
  };
}

